import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { campaignApi, commentApi, notificationApi } from '../api'
import type { Campaign, ScheduledComment, Notification } from '../types'

export function Dashboard() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [pendingComments, setPendingComments] = useState<ScheduledComment[]>([])
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [campaignsData, commentsData, notificationsData] = await Promise.all([
        campaignApi.list(),
        commentApi.listPending(),
        notificationApi.list(),
      ])

      setCampaigns(campaignsData.campaigns)
      setPendingComments(commentsData.comments)
      setNotifications(notificationsData.notifications)
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <div>Loading dashboard...</div>

  const activeCampaigns = campaigns.filter((c) => c.active)
  const unreadNotifications = notifications.filter((n) => !n.read)

  return (
    <div>
      <h2>Dashboard</h2>

      {/* Quick Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/campaigns')}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Active Campaigns</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
            {activeCampaigns.length}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
            of {campaigns.length} total
          </p>
        </div>

        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/comments')}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Pending Approval</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#ffc107' }}>
            {pendingComments.length}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
            comments waiting
          </p>
        </div>

        <div
          style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#666' }}>Notifications</h3>
          <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#007bff' }}>
            {unreadNotifications.length}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
            unread
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '15px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/campaigns/new')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Create Campaign
          </button>
          <button
            onClick={() => navigate('/comments')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Review Comments
          </button>
          <button
            onClick={() => navigate('/analytics')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            View Analytics
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      {unreadNotifications.length > 0 && (
        <div>
          <h3 style={{ marginBottom: '15px' }}>Recent Notifications</h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            {unreadNotifications.slice(0, 5).map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}
              >
                <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{notification.message}</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  {new Date(notification.createdAt).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
